import React, { useEffect, useState} from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../Components/Header";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import "swiper/css/pagination";
import { getHotelRoomDetails, saveLowFareHotels } from "../../Services/HotelSearchApi";


const HotelDetail = () => {
  const navigate = useNavigate();
  const [hotelData, setHotelData] = useState(null);
  const [searchData, setSearchData] = useState(null);
  const [roomData, setRoomData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCancellationPolicy, setSelectedCancellationPolicy] =
    useState(null);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [activeTab, setActiveTab] = useState("ROOMS");

  // Filter States
  const [filters, setFilters] = useState({
    price: [],
    mealType: [],
    refundable: [],
    pan: [],
    searchTerm: "",
    sortBy: "LOWEST", // default sorting
  });

  const fetchedRef = React.useRef(false);

  useEffect(() => {
    // Prevent double hitting the endpoint on mount (e.g. in React.StrictMode)
    if (fetchedRef.current) return;

    // Get hotel and search data from sessionStorage
    const hotel = sessionStorage.getItem("selectedHotel");
    const search = sessionStorage.getItem("hotelSearchData");

    if (!hotel || !search) {
      navigate("/hotel/result");
      return;
    }

    const parsedHotel = JSON.parse(hotel);
    const parsedSearch = JSON.parse(search);

    setHotelData(parsedHotel);
    setSearchData(parsedSearch);

    // Fetch room details
    fetchRoomDetails(parsedHotel, parsedSearch);
    fetchedRef.current = true;
  }, [navigate]);

  const fetchRoomDetails = async (hotel, search) => {
    setLoading(true);

    try {
      const payload = {
        traceId: search.traceId,
        hotelId: hotel.hotelid,
        checkInDate: search.checkInDate,
        checkOutDate: search.checkOutDate,
        roomGuests: search.roomGuests,
        nationality: search.nationality || "IN",
        currency: search.currency || "INR",
        key: search.Key || search.key || "",
        corporateTraveler: search.corporateTraveler || "",
        corporateCode: search.corporateCode || "",
        travelerType: search.travelerType || search.travelType || "",
        supplierParameter: search.supplierParameter || "",
      };

      const response = await getHotelRoomDetails(payload);

      // Update the alternative hotel log with the actual bookingRefNo and save it back for later
      try {
        const storedLog = sessionStorage.getItem("hotelAlternativeLog");
        if (storedLog) {
          const logPayload = JSON.parse(storedLog);
          if (response.bookingRefNo) {
            logPayload.referenceNo = response.bookingRefNo;
            sessionStorage.setItem("hotelAlternativeLog", JSON.stringify(logPayload));
          }
          
          // Call the API endpoint
          await saveLowFareHotels(logPayload);
          // Optional: Remove it so we don't accidentally send it again on reloads
          sessionStorage.removeItem("hotelAlternativeLog");
        }
      } catch (logError) {
        console.error("Failed to process low fare hotel log:", logError);
      }

      // Normalize Room Data
      const rawRooms = response.data?.rooms || response.rooms || [];
      const normalizedRooms = rawRooms.map((room) => {
        let roomImages = [];
        try {
          // If roomImages is a string (common in this API), parse it
          const images =
            typeof room.roomImages === "string"
              ? JSON.parse(room.roomImages)
              : room.roomImages;

          if (Array.isArray(images)) {
            roomImages = images
              .map((img) => {
                // Handle different image object structures
                if (typeof img === "string") return img;
                return (
                  img.Url ||
                  img.links?.XXL?.href ||
                  img.links?.original?.href ||
                  img.links?.large?.href
                );
              })
              .filter(Boolean);
          } else if (room.roomDetails && Array.isArray(room.roomDetails)) {
            // Check roomDetails for images
            room.roomDetails.forEach((detail) => {
              try {
                const detailImages =
                  typeof detail.roomImages === "string"
                    ? JSON.parse(detail.roomImages)
                    : detail.roomImages;
                if (Array.isArray(detailImages)) {
                  detailImages.forEach((img) => {
                    const url =
                      img.Url ||
                      img.links?.XXL?.href ||
                      img.links?.original?.href;
                    if (url) roomImages.push(url);
                  });
                }
              } catch (e) {}
            });
          }
        } catch (e) {
          console.warn("Failed to parse room images:", e);
        }

        return {
          ...room,
          roomImages: roomImages.length > 0 ? roomImages : room.roomImages,
          // Extract room name from roomDetails if available
          roomType:
            room.roomDetails?.[0]?.roomName || room.roomType || "Standard Room",
          // Normalize guests
          guests: room.guests || {
            adults: room.noOfAdults || 2,
            children: room.noOfChilds || 0,
          },
          bookingRefNo: response.bookingRefNo || "",
        };
      });

      setRoomData(normalizedRooms);

      // If the response contains enriched static data, merge it into our hotelData
      if (response.staticData) {
        // Normalize enriched address
        let enrichedAddress = response.staticData.address;
        if (enrichedAddress && typeof enrichedAddress === "object") {
          enrichedAddress =
            enrichedAddress.fulladdr ||
            enrichedAddress.Adr ||
            `${enrichedAddress.line1 || ""}, ${enrichedAddress.city || ""}`;
        }

        setHotelData((prev) => ({
          ...prev,
          ...response.staticData,
          address: enrichedAddress || prev.address,
          // Extract specific parsed descriptions if available
          enrichedDescription: response.staticData.descriptions,
          enrichedPolicies: response.staticData.hotelPolicies,
        }));
      }
    } catch (error) {
      console.error("Failed to fetch room details:", error);
      // Mock data for development based on the design document
      setRoomData([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateNights = (inDate, outDate) => {
    if (!inDate || !outDate) return 1;
    try {
      const start = new Date(inDate);
      const end = new Date(outDate);
      const diff = end.getTime() - start.getTime();
      const nights = Math.ceil(diff / (1000 * 3600 * 24));
      return nights > 0 ? nights : 1;
    } catch (e) {
      return 1;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const options = { day: "2-digit", month: "short", year: "numeric" };
      return date.toLocaleDateString("en-GB", options);
    } catch (e) {
      return dateString;
    }
  };

  const formatPolicyDate = (dateString, includeYear = true, defaultTime = "12:00 AM") => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = date.getDate();
      const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
      };
      const dayStr = getOrdinal(day);
      const month = date.toLocaleDateString("en-US", { month: "long" });
      const year = date.getFullYear();

      let hasTime = false;
      if (typeof dateString === "string") {
        hasTime = dateString.includes("T") || dateString.includes(":") || (dateString.includes(" ") && dateString.split(" ").length > 1);
      }
      
      let timeStr = defaultTime;
      if (hasTime) {
        let hours = date.getHours();
        let minutes = date.getMinutes();
        let ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12;
        hours = hours ? hours : 12;
        const minStr = minutes < 10 ? "0" + minutes : minutes;
        timeStr = `${hours}:${minStr} ${ampm}`;
      }

      if (includeYear) {
        return `${dayStr} ${month}, ${year}, ${timeStr}`;
      } else {
        return `${dayStr} ${month} ${timeStr}`;
      }
    } catch (e) {
      return dateString;
    }
  };


  const formatCancellationPolicy = (policy) => {
    if (!policy) return "No cancellation policy available.";
    if (typeof policy === "string") return policy;
    if (Array.isArray(policy)) {
      return policy
        .map((p) => {
          if (typeof p === "object" && p.fromDate) {
            const amount =
              p.penaltyAmount === 0
                ? "Free cancellation"
                : `Penalty: ${p.currency || "INR"} ${Math.round(p.penaltyAmount).toLocaleString()}`;
            return `${amount} from ${formatDate(p.fromDate)} to ${formatDate(p.toDate)}`;
          }
          return p;
        })
        .join(". ");
    }
    return JSON.stringify(policy);
  };

  const getAmenityIcon = (label) => {
    const l = label.toLowerCase();
    if (l.includes("wifi") || l.includes("internet")) return "fa-wifi";
    if (l.includes("pool")) return "fa-swimming-pool";
    if (l.includes("parking") || l.includes("valet")) return "fa-car";
    if (
      l.includes("restaurant") ||
      l.includes("breakfast") ||
      l.includes("dining")
    )
      return "fa-utensils";
    if (l.includes("spa") || l.includes("massage") || l.includes("wellness"))
      return "fa-spa";
    if (l.includes("gym") || l.includes("fitness")) return "fa-dumbbell";
    if (l.includes("air conditioning") || l.includes("snowflake"))
      return "fa-snowflake";
    if (l.includes("beach")) return "fa-umbrella-beach";
    if (l.includes("tennis")) return "fa-table-tennis";
    if (l.includes("bar") || l.includes("lounge"))
      return "fa-glass-martini-alt";
    return "fa-check-circle";
  };

  const handleFilterToggle = (category, value) => {
    setFilters((prev) => {
      const current = prev[category];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [category]: next };
    });
  };

  const filterOptions = React.useMemo(() => {
    const prices = roomData.map((r) => r.price).filter(Boolean);
    const minP = Math.min(...prices, 0);
    const maxP = Math.max(...prices, 0);

    let dynamicPriceRanges = [];
    if (prices.length > 1) {
      const rangeCount = 3;
      const step = Math.ceil((maxP - minP) / rangeCount);
      dynamicPriceRanges = Array.from({ length: rangeCount }, (_, i) => {
        const start = Math.round(minP + i * step);
        const end = Math.round(i === rangeCount - 1 ? maxP : start + step);
        return {
          id: i.toString(),
          label: `${start.toLocaleString()} - ${end.toLocaleString()}`,
          start,
          end,
        };
      });
    }

    return {
      priceRanges: dynamicPriceRanges,
      mealTypes: [
        ...new Set(
          roomData.flatMap((r) =>
            Array.isArray(r.mealBasis) ? r.mealBasis : [r.mealBasis],
          ),
        ),
      ].filter(Boolean),
      refundableOptions: ["Refundable", "Non Refundable"],
      panOptions: ["PAN Required", "No PAN Required"],
    };
  }, [roomData]);

  const filteredRooms = React.useMemo(() => {
    return roomData
      .filter((room) => {
        // Price Filter
        if (filters.price.length > 0) {
          const price = room.price || 0;
          const priceMatches = filters.price.some((rangeId) => {
            const range = filterOptions.priceRanges.find(
              (r) => r.id === rangeId,
            );
            return range ? price >= range.start && price <= range.end : false;
          });
          if (!priceMatches) return false;
        }

        // Meal Type Filter
        if (filters.mealType.length > 0) {
          const roomMeals = Array.isArray(room.mealBasis)
            ? room.mealBasis
            : [room.mealBasis];
          const hasMatch = filters.mealType.some((f) => roomMeals.includes(f));
          if (!hasMatch) return false;
        }

        // Refundable Filter
        if (filters.refundable.length > 0) {
          const isRef = room.refundable ? "Refundable" : "Non Refundable";
          if (!filters.refundable.includes(isRef)) return false;
        }

        // PAN Filter
        if (filters.pan.length > 0) {
          const isPan = room.isPanRequired ? "PAN Required" : "No PAN Required";
          if (!filters.pan.includes(isPan)) return false;
        }

        // Search Filter
        if (filters.searchTerm) {
          const firstRoomName =
            room.roomDetails?.[0]?.roomName || room.roomType || "";
          if (
            !firstRoomName
              .toLowerCase()
              .includes(filters.searchTerm.toLowerCase())
          ) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        const priceA = a.price || 0;
        const priceB = b.price || 0;
        if (filters.sortBy === "LOWEST") return priceA - priceB;
        if (filters.sortBy === "HIGHEST") return priceB - priceA;
        return 0;
      });
  }, [roomData, filters, filterOptions]);

  const handleBookRoom = (room) => {
    sessionStorage.setItem("selectedRoom", JSON.stringify(room));
    navigate("/hotel/hotelbooking");
  };

  const handleViewCancellation = (policy) => {
    setSelectedCancellationPolicy(policy);
    setShowCancellationModal(true);
  };

  if (!hotelData || !searchData) {
    return null;
  }

  const nightsCount = calculateNights(
    searchData.checkInDate,
    searchData.checkOutDate,
  );

  return (
    <>
      <Header />
      <section className="Booking_hotel">
        <div className="container_box">
          <div className="row mb-3 mt-2">
            <div className="col-12">
              <button 
                className="btn-choose-room d-inline-flex align-items-center gap-2"
                onClick={() => navigate("/hotel/result")}
              >
                <i className="fas fa-arrow-left"></i> Back to Search
              </button>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-12 col-lg-12">
              <div className="box-shadow-main mb-4 hotl_nm">
                <div className="hotel-header-info p-5">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="hotel-name-section">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <h1 className="hotel-h1">{hotelData.hotelName}</h1>
                        <div className="start_cat d-flex gap-1 align-items-center mb-1">
                          {[...Array(Number(hotelData.starRating) || 0)].map(
                            (_, i) => (
                              <i key={i} className="fas fa-star star-icon"></i>
                            ),
                          )}
                        </div>
                      </div>
                      <p className="address-text d-inline-flex align-items-center gap-2 flex-wrap">
                        {typeof hotelData.address === "object"
                          ? hotelData.address?.fulladdr ||
                            hotelData.address?.Adr ||
                            JSON.stringify(hotelData.address)
                          : hotelData.address}
                        <a
                          href="#location"
                          className="show-map-link text-decoration-underline ms-2"
                          style={{
                            color: "#295eff",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveTab("LOCATION");
                            document
                              .getElementById("hotel-tabs-anchor")
                              ?.scrollIntoView({ behavior: "smooth" });
                          }}
                        >
                          <i className="fas fa-map-marker-alt me-1"></i> Show on map
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="hotel-dates-row d-flex flex-wrap align-items-center justify-content-end gap-3 px-4 py-2 bg-white border-top">
                    <div className="travel-info-item d-flex align-items-center">
                      <span
                        className="label d-inline-block me-2 fw-bold"
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          fontWeight: "700",
                          color: "black",
                        }}
                      >
                        Check-In:
                      </span>
                      <div className="premium-meta-box min-w-120">
                        <span className="meta-value">
                          {formatDate(searchData.checkInDate)}
                        </span>
                      </div>
                    </div>
                    <div className="travel-info-item d-flex align-items-center">
                      <span className="travel-label d-inline-block me-2">
                        Check-Out:
                      </span>
                      <div
                        className="premium-meta-box text-center d-flex align-items-center justify-content-center"
                        style={{
                          background: "#110942",
                          border: "none",
                          minWidth: "120px",
                          height: "32px",
                          borderRadius: "4px",
                        }}
                      >
                        <span className="meta-value">
                          {formatDate(searchData.checkOutDate)}
                        </span>
                      </div>
                    </div>
                    <div className="travel-info-item d-flex align-items-center">
                      <span
                        className="label d-inline-block me-2 fw-bold"
                        style={{
                          fontSize: "12px",
                          textTransform: "uppercase",
                          fontWeight: "700",
                          color: "black",
                        }}
                      >
                        Guests:
                      </span>
                      <div className="premium-meta-box min-w-140">
                        <span className="meta-value">
                          {searchData.roomGuests?.length || 1} Room,{" "}
                          {searchData.roomGuests?.reduce(
                            (sum, r) => sum + r.noOfAdults,
                            0,
                          ) || 1}{" "}
                          Guests
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hotel Images */}
            <div className="col-12 col-md-8 col-lg-8 mb-4 mb-md-0">
              <div className="hotl_big_img">
                {(() => {
                  // Prefer room-specific images if available, otherwise use all normalized images
                  const galleryImages =
                    hotelData.roomDetails?.images &&
                    hotelData.roomDetails.images.length > 0
                      ? hotelData.roomDetails.images
                          .map((img) => img.links?.original?.href)
                          .filter(Boolean)
                      : hotelData.allImages || [];

                  const finalImages = galleryImages.filter(Boolean);

                  if (finalImages.length > 0) {
                    return (
                      <Swiper
                        modules={[Navigation, Autoplay]}
                        spaceBetween={0}
                        slidesPerView={1}
                        navigation
                        autoplay={{ delay: 3000 }}
                        loop={finalImages.length > 1}
                        className="full-size-swiper"
                      >
                        {finalImages.map((img, idx) => (
                          <SwiperSlide key={idx}>
                            <img
                              src={img}
                              alt={`hotel-${idx}`}
                              className="img-cover-full"
                              onError={(e) => {
                                e.target.src = "/assets/images/hotel/hotel.jpg";
                              }}
                            />
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    );
                  }
                  return (
                    <img
                      src="/assets/images/hotel/hotel-big.jpg"
                      alt="hotel"
                      className="img-cover-full"
                    />
                  );
                })()}
              </div>
            </div>
            <div className="col-12 col-md-4 col-lg-4">
              {(() => {
                const sidebarImages = hotelData.roomDetails?.images
                  ? hotelData.roomDetails.images
                      .slice(1, 3)
                      .map((img) => img.links?.original?.href)
                  : [hotelData.allImages?.[1], hotelData.allImages?.[2]];

                return (
                  <>
                    <div className="small_top shadow-sm">
                      <img
                        src={
                          sidebarImages[0] ||
                          "/assets/images/hotel/hotel-top.jpg"
                        }
                        alt="hotel"
                        onError={(e) => {
                          e.target.src = "/assets/images/hotel/hotel-top.jpg";
                        }}
                        style={{ objectFit: "cover" }}
                        className="img-fluid w-100 h-100"
                      />
                    </div>
                    <div className="small_botom shadow-sm">
                      <img
                        src={
                          sidebarImages[1] ||
                          "/assets/images/hotel/hotel-botom.jpg"
                        }
                        alt="hotel"
                        onError={(e) => {
                          e.target.src = "/assets/images/hotel/hotel-botom.jpg";
                        }}
                        style={{ objectFit: "cover" }}
                        className="img-fluid w-100 h-100"
                      />
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Amenities Grid Section */}
        <div className="row mt-5">
          <div className="col-12 col-md-12">
            <div className="amenities-grid-section">
              <div className="amenities-grid">
                {(
                  hotelData.amenities || [
                    "Very Good Breakfast",
                    "Outdoor swimming pool",
                    "2 restaurants",
                    "Free on-site parking",
                    "Spa and wellness centre",
                    "Private bathroom",
                    "View",
                    "Free WiFi",
                    "Room service",
                    "Air conditioning",
                  ]
                )
                  .slice(0, 10)
                  .map((amenity, idx) => (
                    <div key={idx} className="amenity-item">
                      <div className="amenity-icon">
                        <i className={`fa ${getAmenityIcon(amenity)}`}></i>
                      </div>
                      <div className="amenity-label">{amenity}</div>
                    </div>
                  ))}
              </div>
              <a
                href="#amenities"
                className="view-more-link"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("FACILITY");
                  document
                    .getElementById("hotel-tabs-anchor")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                view more
              </a>
            </div>
          </div>
        </div>

        {/* Tabs and Content Section */}
        <div className="row mt-4" id="hotel-tabs-anchor">
          <div className="col-12 col-md-12 col-lg-12">
            <div className="hotel-tabs-container">
              <ul className="nav nav-tabs hotel-nav-tabs nav-justified w-100 border-0">
                {["ROOMS", "ABOUT", "FACILITY", "LOCATION", "POLICIES"].map(
                  (tab) => (
                    <li key={tab} className="nav-item flex-fill">
                      <button
                        className={`nav-link w-100 ${activeTab === tab ? "active" : ""}`}
                        style={{
                          borderRadius: "0",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          fontSize: "14px",
                          padding: "15px 0",
                        }}
                        onClick={() => setActiveTab(tab)}
                      >
                        {tab}
                      </button>
                    </li>
                  ),
                )}
              </ul>

              <div className="tab-content mt-3">
                {/* ROOMS Tab */}
                {activeTab === "ROOMS" && (
                  <div className="row">
                    {/* Filter Section */}
                    {/* Horizontal Room Filter Bar (Premium Design) */}
                    <div className="col-12 col-md-12 col-lg-12">
                      <div className="premium-filter-bar mb-4 p-3 bg-white rounded shadow-sm d-flex flex-wrap align-items-center gap-3">
                        <div className="filter-main-title pe-3">
                          <h5 className="mb-0 fw-bold">Room types</h5>
                          <small className="text-muted">
                            Showing {filteredRooms.length} options
                          </small>
                        </div>

                        <div className="d-flex flex-wrap align-items-center gap-3 flex-grow-1 filter-bar-actions">
                          {/* Price Sorting Arrows */}
                          {/* <div className="dropdown">
                            <button
                              className="btn btn-outline-secondary dropdown-toggle btn-sm h-100 d-flex align-items-center gap-2"
                              data-bs-toggle="dropdown"
                              style={{ height: "40px", borderRadius: "20px" }}
                            >
                              <i
                                className={`fas ${filters.sortBy === "LOWEST" ? "fa-sort-amount-down-alt" : "fa-sort-amount-up"}`}
                              ></i>
                              Price
                            </button>
                            <div className="dropdown-menu shadow border-0 p-2">
                              <button
                                className={`dropdown-item rounded mb-1 d-flex align-items-center gap-2 ${filters.sortBy === "LOWEST" ? "bg-light fw-bold" : ""}`}
                                onClick={() =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    sortBy: "LOWEST",
                                  }))
                                }
                              >
                                <i className="fas fa-arrow-down text-success small"></i>{" "}
                                Lowest to Highest
                              </button>
                              <button
                                className={`dropdown-item rounded d-flex align-items-center gap-2 ${filters.sortBy === "HIGHEST" ? "bg-light fw-bold" : ""}`}
                                onClick={() =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    sortBy: "HIGHEST",
                                  }))
                                }
                              >
                                <i className="fas fa-arrow-up text-danger small"></i>{" "}
                                Highest to Lowest
                              </button>
                            </div>
                          </div> */}

                          <div
                            className="filter-search-rooms position-relative"
                            style={{ width: "100%", maxWidth: "250px" }}
                          >
                            <i
                              className="fas fa-search position-absolute"
                              style={{
                                left: "15px",
                                top: "39%",
                                transform: "translateY(-50%)",
                                color: "#aaa",
                                zIndex: 5,
                              }}
                            ></i>
                            <input
                              type="text"
                              className="form-control"
                              style={{
                                borderRadius: "20px",
                                border: "1px solid #ddd",
                                boxShadow: "0 2px 10px #dacd65",
                                paddingLeft: "42px",
                                height: "40px",
                              }}
                              placeholder="Search rooms..."
                              value={filters.searchTerm}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  searchTerm: e.target.value,
                                }))
                              }
                            />
                          </div>

                          <div className="d-flex flex-wrap gap-2 ms-auto filter-dropdowns">
                            {/* Refundable Tag Component */}
                            <div className="dropdown">
                              <button
                                className="btn btn-outline-secondary dropdown-toggle btn-sm h-100"
                                data-bs-toggle="dropdown"
                              >
                                Refundable
                              </button>
                              <div
                                className="dropdown-menu p-3"
                                style={{ minWidth: "200px" }}
                              >
                                {filterOptions.refundableOptions.map((opt) => (
                                  <div className="form-check mb-2" key={opt}>
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={filters.refundable.includes(opt)}
                                      onChange={() =>
                                        handleFilterToggle("refundable", opt)
                                      }
                                      id={`ref-${opt}`}
                                    />
                                    <label
                                      className={
                                        "form-check-label small fw-bold " +
                                        (opt === "Refundable"
                                          ? "text-success"
                                          : "text-danger")
                                      }
                                      htmlFor={`ref-${opt}`}
                                    >
                                      {opt}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* PAN Required Dropdown */}
                            <div className="dropdown">
                              <button
                                className="btn btn-outline-secondary dropdown-toggle btn-sm h-100"
                                data-bs-toggle="dropdown"
                              >
                                PAN
                              </button>
                              <div
                                className="dropdown-menu p-3 shadow border-0"
                                style={{ minWidth: "200px" }}
                              >
                                {filterOptions.panOptions.map((opt) => (
                                  <div className="form-check mb-2" key={opt}>
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={filters.pan.includes(opt)}
                                      onChange={() =>
                                        handleFilterToggle("pan", opt)
                                      }
                                      id={`pan-${opt}`}
                                    />
                                    <label
                                      className="form-check-label text-dark small"
                                      htmlFor={`pan-${opt}`}
                                    >
                                      {opt}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Meal Plan Dropdown */}
                            <div className="dropdown">
                              <button
                                className="btn btn-outline-secondary dropdown-toggle btn-sm h-100"
                                data-bs-toggle="dropdown"
                              >
                                Meal Plans
                              </button>
                              <div
                                className="dropdown-menu p-3"
                                style={{ minWidth: "200px" }}
                              >
                                {filterOptions.mealTypes.map((opt) => (
                                  <div className="form-check mb-2" key={opt}>
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={filters.mealType.includes(opt)}
                                      onChange={() =>
                                        handleFilterToggle("mealType", opt)
                                      }
                                      id={`meal-${opt}`}
                                    />
                                    <label
                                      className="form-check-label text-dark small"
                                      htmlFor={`meal-${opt}`}
                                    >
                                      {opt}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Price Filter Tag */}
                            <div className="dropdown">
                              <button
                                className="btn btn-outline-secondary dropdown-toggle btn-sm h-100 d-flex align-items-center gap-2"
                                data-bs-toggle="dropdown"
                                style={{ height: "40px", borderRadius: "20px" }}
                              >
                                <i
                                  className={`fas ${filters.sortBy === "LOWEST" ? "fa-sort-amount-down-alt" : "fa-sort-amount-up"}`}
                                ></i>
                                Price
                              </button>
                              <div className="dropdown-menu shadow border-0 p-2">
                                <button
                                  className={`dropdown-item rounded mb-1 d-flex align-items-center gap-2 ${filters.sortBy === "LOWEST" ? "bg-light fw-bold" : ""}`}
                                  onClick={() =>
                                    setFilters((prev) => ({
                                      ...prev,
                                      sortBy: "LOWEST",
                                    }))
                                  }
                                >
                                  <i className="fas fa-arrow-down text-success small"></i>{" "}
                                  Lowest to Highest
                                </button>
                                <button
                                  className={`dropdown-item rounded d-flex align-items-center gap-2 ${filters.sortBy === "HIGHEST" ? "bg-light fw-bold" : ""}`}
                                  onClick={() =>
                                    setFilters((prev) => ({
                                      ...prev,
                                      sortBy: "HIGHEST",
                                    }))
                                  }
                                >
                                  <i className="fas fa-arrow-up text-danger small"></i>{" "}
                                  Highest to Lowest
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Rooms List */}
                    <div className="col-12 col-md-12 col-lg-12">
                      {loading ? (
                        <div className="text-center py-5">
                          <p className="text-center load">
                            <span className="loader"></span>
                          </p>
                          <h5 className="mt-3 text-muted">
                            Fetching room details...
                          </h5>
                        </div>
                      ) : filteredRooms.length > 0 ? (
                        <div className="room-selection-container">
                          {filteredRooms.map((room, index) => {
                            const roomDetails = room.roomDetails || [];
                            const freeCancelDate = (() => {
                              if (!room.cancellationPolicy || !Array.isArray(room.cancellationPolicy)) return null;
                              const freePolicy = room.cancellationPolicy.find(
                                (p) => p.penaltyAmount === 0 || String(p.penaltyAmount) === "0"
                              );
                              return freePolicy?.toDate || null;
                            })();
                            const roomCount = roomDetails.length;

                            let displayRoomName = "";
                            if (roomCount > 1) {
                              // If multiple rooms, check if they are the same type
                              const uniqueNames = [
                                ...new Set(roomDetails.map((d) => d.roomName)),
                              ];
                              if (uniqueNames.length === 1) {
                                displayRoomName = `${roomCount} * ${uniqueNames[0]}`;
                              } else {
                                // If different types, list them
                                displayRoomName = roomDetails
                                  .map((d) => d.roomName)
                                  .join(" + ");
                              }
                            } else {
                              displayRoomName =
                                roomDetails[0]?.roomName ||
                                room.roomType ||
                                "Standard Room";
                            }

                            return (
                              <div
                                key={index}
                                className="room-selection-card premium shadow-sm"
                              >
                                <div className="row align-items-center">
                                  {/* Column 1: Room Image Carousel */}
                                  <div className="col-12 col-md-6 col-lg-3 mb-3 mb-lg-0">
                                    <div className="room-carousel-wrapper shadow-sm">
                                      {room.roomImages &&
                                      room.roomImages.length > 0 ? (
                                        <Swiper
                                          modules={[Navigation, Pagination]}
                                          spaceBetween={10}
                                          slidesPerView={1}
                                          navigation
                                          pagination={{ clickable: true }}
                                          loop={room.roomImages.length > 1}
                                          className="room-swiper"
                                        >
                                          {room.roomImages.map((img, idx) => (
                                            <SwiperSlide key={idx}>
                                              <div className="room-slide-image">
                                                <img
                                                  src={img}
                                                  alt={`room-${idx}`}
                                                  onError={(e) => {
                                                    e.target.src =
                                                      "/assets/images/hotel/hotel.jpg";
                                                  }}
                                                />
                                              </div>
                                            </SwiperSlide>
                                          ))}
                                        </Swiper>
                                      ) : (
                                        <div className="room-slide-image">
                                          <img
                                            src="/assets/images/hotel/hotel.jpg"
                                            alt="room"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Column 2: Room Type and Specification */}
                                  <div className="col-12 col-md-6 col-lg-3 mb-3 mb-lg-0">
                                    <div className="room-info-section">
                                      <h5
                                        className="room-title mb-2"
                                        style={{
                                          color: "#dacd65",
                                          fontWeight: "700",
                                        }}
                                      >
                                        {displayRoomName}
                                      </h5>
                                      <div className="room-features-list">
                                        <div
                                          className="small mb-2 fw-bold d-flex align-items-center"
                                          style={{ color: "#110942" }}
                                        >
                                          <i className="fa fa-users me-2"></i>{" "}
                                          {room.guests?.adults || 2} Adults{" "}
                                          {room.guests?.children > 0
                                            ? `, ${room.guests.children} Children`
                                            : ""}
                                        </div>
                                        <div
                                          className="small mb-2 fw-bold d-flex align-items-center"
                                          style={{ color: "#110942" }}
                                        >
                                          <i className="fa fa-utensils me-2"></i>{" "}
                                          {Array.isArray(room.mealBasis)
                                            ? room.mealBasis.join(", ")
                                            : room.mealBasis || "Room Only"}
                                        </div>
                                        <div
                                          className="pan-status mt-2 pt-2 border-top"
                                          style={{ borderColor: "#f0f0f0" }}
                                        >
                                          <div
                                            className="d-flex align-items-center fw-bold"
                                            style={{ color: "#110942" }}
                                          >
                                            <i
                                              className="fas fa-id-card me-2"
                                              style={{ fontSize: "14px" }}
                                            ></i>
                                            <span
                                              style={{ fontSize: "12.5px" }}
                                            >
                                              PAN Card:{" "}
                                              {room.isPanRequired
                                                ? "Required"
                                                : "Not Required"}
                                            </span>
                                          </div>
                                        </div>
                                        {freeCancelDate && (
                                          <div className="text-success small fw-bold mt-2 d-flex align-items-center" style={{ fontSize: "12.5px" }}>
                                            <i className="fas fa-check-circle me-1" style={{ color: "#22c55e" }}></i> Free Cancellation before {formatDate(freeCancelDate)}
                                          </div>
                                        )}
                                        {/* {room.gst && room.priceDetails?.gstAmount && (
                                          <div className="mt-2">
                                            <div 
                                              className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded shadow-sm" 
                                              style={{ 
                                                background: "#fff8f0", 
                                                border: "1px solid #ffe8cc", 
                                                color: "#d97706",
                                                fontSize: "12px", 
                                                fontWeight: "600",
                                                borderRadius: "4px"
                                              }}
                                            >
                                              <i className="fas fa-percent" style={{ fontSize: "11px" }}></i> GST claim of ₹{Math.round(room.priceDetails.gstAmount).toLocaleString()} eligible
                                            </div>
                                          </div>
                                        )} */}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Column 3: Middle Section - Policies & Requirements */}
                                  <div
                                    className="col-12 col-md-6 col-lg-3 mb-3 mb-lg-0 d-flex flex-column justify-content-center align-items-center border-start-lg border-end-lg"
                                    style={{
                                      borderLeft: "1px solid #f0f0f0",
                                      borderRight: "1px solid #f0f0f0",
                                    }}
                                  >
                                    <div className="policy-section text-center p-2">
                                      <div
                                        className={
                                          room.refundable
                                            ? "refundable-label"
                                            : "non-refundable-label"
                                        }
                                      >
                                        {room.refundable
                                          ? "REFUNDABLE"
                                          : "NON-REFUNDABLE"}
                                      </div>

                                      <div className="cancellation-link">
                                        <button
                                          className="btn btn-link btn-sm p-0 text-decoration-none fw-bold"
                                          style={{
                                            fontSize: "14px",
                                            color: "#295eff",
                                          }}
                                          onClick={() =>
                                            handleViewCancellation(
                                              room.cancellationPolicy,
                                            )
                                          }
                                        >
                                          <i className="fas fa-info-circle me-1"></i>{" "}
                                          View Policy
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Column 4: Price and Book */}
                                  <div className="col-12 col-md-6 col-lg-3 text-lg-end text-center d-flex flex-column justify-content-center">
                                    <div className="price-box p-2">
                                      <div
                                        className="text-muted small mb-1"
                                        style={{ fontSize: "11px" }}
                                      >
                                        Price for 1 night
                                      </div>
                                      <h2
                                        className="room-price mb-3"
                                        style={{
                                          fontSize: "26px",
                                          fontWeight: "800",
                                          color: "#110942",
                                        }}
                                      >
                                        <span
                                          className="currency-symbol"
                                          style={{
                                            fontSize: "18px",
                                            marginRight: "4px",
                                            // color: "#0f7427aa",
                                          }}
                                        >
                                          ₹
                                        </span>
                                        {Math.round(
                                          room.price || 0,
                                        ).toLocaleString()}
                                      </h2>
                                      {room.policyType && (
                                        <div
                                          className="text-right
                                         mb-2"
                                        >
                                          <span
                                            className={`fw-semibold ${room.policyType === "OutOfPolicy" ? "text-danger" : "text-success"}`}
                                            style={{ fontSize: "12px" }}
                                          >
                                            {room.policyType ===
                                            "OutOfPolicy" ? (
                                              <>
                                                <i className="fa fa-times-circle me-1"></i>{" "}
                                                OutOfPolicy
                                              </>
                                            ) : (
                                              <>
                                                <i className="fa fa-check-circle me-1"></i>{" "}
                                                WithinPolicy
                                              </>
                                            )}
                                          </span>
                                        </div>
                                      )}
                                      <button
                                        className="btn-choose-room"
                                        onClick={() => handleBookRoom(room)}
                                      >
                                        Choose Room
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="alert alert-info">
                          No rooms available matching your filters.
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ABOUT Tab */}
                {activeTab === "ABOUT" && (
                  <div className="details_viw">
                    <div className="details_viw">
                      {/* Enriched Description from Room Details API */}
                      {hotelData.enrichedDescription ? (
                        <>
                          {hotelData.enrichedDescription.headline && (
                            <div className="mb-4">
                              <h5 className="fw-bold text-primary">Headline</h5>
                              <p className="pre-line">
                                {hotelData.enrichedDescription.headline}
                              </p>
                            </div>
                          )}
                          {hotelData.enrichedDescription.default &&
                            (() => {
                              try {
                                const descJson = JSON.parse(
                                  hotelData.enrichedDescription.default,
                                );
                                return Object.entries(descJson).map(
                                  ([key, value]) => {
                                    if (key === "headline" || !value)
                                      return null;
                                    return (
                                      <div key={key} className="mb-3">
                                        <h6 className="text-capitalize fw-bold">
                                          {key.replace(/_/g, " ")}
                                        </h6>
                                        <p className="small text-muted">
                                          {value}
                                        </p>
                                      </div>
                                    );
                                  },
                                );
                              } catch (e) {
                                return (
                                  <p>{hotelData.enrichedDescription.default}</p>
                                );
                              }
                            })()}
                        </>
                      ) : hotelData.description &&
                        typeof hotelData.description === "object" ? (
                        /* Normalized Description from Static API */
                        Object.entries(hotelData.description).map(
                          ([key, value]) => {
                            if (!value) return null;
                            // Format keys like "on-site_payments" to "On-site Payments"
                            const label = key
                              .replace(/_/g, " ")
                              .replace(/-/g, " ")
                              .replace(/([A-Z])/g, " $1")
                              .trim()
                              .replace(/\b\w/g, (c) => c.toUpperCase());

                            return (
                              <div key={key} className="mb-4">
                                <h6 className="fw-bold text-primary">
                                  {label}
                                </h6>
                                <p className="small text-muted pre-line">
                                  {typeof value === "string"
                                    ? value
                                    : JSON.stringify(value)}
                                </p>
                              </div>
                            );
                          },
                        )
                      ) : (
                        <p>
                          {hotelData.description || "No description available."}
                        </p>
                      )}

                      {/* Hotel Contact Section (New) */}
                      {hotelData.contact &&
                        (hotelData.contact.Ph || hotelData.contact.Wb) && (
                          <div className="mt-5 pt-4 border-top">
                            <h6 className="fw-bold mb-3">
                              Hotel Contact Details
                            </h6>
                            <div className="d-flex gap-4 flex-wrap">
                              {hotelData.contact.Ph && (
                                <div className="contact-item">
                                  <i className="fas fa-phone-alt text-primary me-2"></i>
                                  <span className="small">
                                    {hotelData.contact.Ph}
                                  </span>
                                </div>
                              )}
                              {hotelData.contact.Fax && (
                                <div className="contact-item">
                                  <i className="fas fa-fax text-primary me-2"></i>
                                  <span className="small">
                                    {hotelData.contact.Fax}
                                  </span>
                                </div>
                              )}
                              {hotelData.contact.Wb && (
                                <div className="contact-item">
                                  <i className="fas fa-globe text-primary me-2"></i>
                                  <a
                                    href={hotelData.contact.Wb}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="small text-decoration-none"
                                  >
                                    Visit Website
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* FACILITY Tab */}
                {activeTab === "FACILITY" && (
                  <div className="details_viw">
                    {hotelData.amenities && hotelData.amenities.length > 0 ? (
                      <div className="row amenities_list mt-2">
                        {hotelData.amenities.map((amenity, i) => (
                          <div
                            key={i}
                            className="col-12 col-md-4 col-sm-6 mb-2 d-flex align-items-center"
                          >
                            <span
                              className="me-2"
                              style={{ color: "#dacd65", fontSize: "1.2rem" }}
                            >
                              •
                            </span>
                            <span className="small text-gray-700">
                              {amenity}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <i
                          className="fas fa-concierge-bell text-muted opacity-25 mb-3"
                          style={{ fontSize: "48px" }}
                        ></i>
                        <h5 className="text-muted">No Facilities Data Found</h5>
                        <p className="small text-muted">
                          Information about hotel facilities is currently
                          unavailable.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* LOCATION Tab */}
                {activeTab === "LOCATION" &&
                  (() => {
                    const lat =
                      hotelData.address?.coordinates?.lat || hotelData.latitude;
                    const lng =
                      hotelData.address?.coordinates?.long ||
                      hotelData.longitude;
                    const addressStr =
                      typeof hotelData.address === "object"
                        ? hotelData.address?.fulladdr
                        : hotelData.address;

                    if (!lat && !lng && !addressStr) {
                      return (
                        <div className="details_viw text-center py-5">
                          <i
                            className="fas fa-map-marked-alt text-muted opacity-25 mb-3"
                            style={{ fontSize: "48px" }}
                          ></i>
                          <h5 className="text-muted">No Location Data Found</h5>
                          <p className="small text-muted">
                            The location and map for this property are
                            unavailable.
                          </p>
                        </div>
                      );
                    }

                    // q=${lat},${lng} works better for specific coordinates, fallback to address if missing
                    const query =
                      lat && lng
                        ? `${lat},${lng}`
                        : encodeURIComponent(addressStr);
                    const googleMapsUrl =
                      lat && lng
                        ? `https://www.google.com/maps?q=${lat},${lng}`
                        : `https://www.google.com/maps?q=${encodeURIComponent(addressStr)}`;

                    return (
                      <div className="details_viw">
                        <div className="location-map-container position-relative">
                          <iframe
                            src={`https://maps.google.com/maps?q=${query}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                            width="100%"
                            height="450"
                            style={{ border: 0, borderRadius: "12px" }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Hotel Location"
                            className="shadow-sm"
                          ></iframe>

                          <div className="mt-3 text-end">
                            <button
                              className="btn btn-outline-primary btn-sm rounded-pill px-4"
                              onClick={() =>
                                window.open(googleMapsUrl, "_blank")
                              }
                            >
                              <i className="fas fa-external-link-alt me-2"></i>{" "}
                              View on Google Maps
                            </button>
                          </div>
                        </div>

                        <div className="location-address-info mt-4 p-3 bg-light rounded">
                          <h6 className="fw-bold mb-2">
                            <i className="fas fa-map-marker-alt text-primary me-2"></i>
                            Address
                          </h6>
                          <p className="mb-0 text-muted">{addressStr}</p>
                          {hotelData.address?.phone && (
                            <p className="mb-0 mt-2 text-muted small">
                              <i className="fas fa-phone-alt text-primary me-2"></i>
                              {hotelData.address.phone}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                {/* POLICIES Tab */}
                {activeTab === "POLICIES" && (
                  <div className="details_viw">
                    <div className="policies-container">
                      {hotelData.enrichedPolicies ? (
                        <>
                          {hotelData.enrichedPolicies.checkInCheckOut && (
                            <div className="policy-block">
                              <h5>Check-in & Check-out</h5>
                              <div className="d-flex gap-4 flex-wrap">
                                <div className="policy-item">
                                  <i className="fas fa-sign-in-alt text-success"></i>
                                  <span>
                                    Check-in:{" "}
                                    <strong>
                                      {
                                        hotelData.enrichedPolicies
                                          .checkInCheckOut.checkIn
                                      }
                                    </strong>
                                  </span>
                                </div>
                                <div className="policy-item">
                                  <i className="fas fa-sign-out-alt text-danger"></i>
                                  <span>
                                    Check-out:{" "}
                                    <strong>
                                      {
                                        hotelData.enrichedPolicies
                                          .checkInCheckOut.checkOut
                                      }
                                    </strong>
                                  </span>
                                </div>
                                <div className="policy-item">
                                  <i className="fas fa-user-clock text-primary"></i>
                                  <span>
                                    Min Age:{" "}
                                    <strong>
                                      {
                                        hotelData.enrichedPolicies
                                          .checkInCheckOut.checkinMinAge
                                      }
                                    </strong>
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {[
                            "specialInstructions",
                            "knowBeforeYouGo",
                            "mandatoryFees",
                          ].map((key) => {
                            const policyStr = hotelData.enrichedPolicies[key];
                            if (!policyStr) return null;
                            try {
                              const parsed = JSON.parse(policyStr);
                              return Object.entries(parsed).map(
                                ([subKey, subValue]) => (
                                  <div key={subKey} className="policy-block">
                                    <h5 className="text-capitalize">
                                      {subKey.replace(/_/g, " ")}
                                    </h5>
                                    <p
                                      className="small text-muted"
                                      style={{ whiteSpace: "pre-line" }}
                                    >
                                      {subValue}
                                    </p>
                                  </div>
                                ),
                              );
                            } catch (e) {
                              return (
                                <div key={key} className="policy-block">
                                  <h5 className="text-capitalize">
                                    {key.replace(/([A-Z])/g, " $1")}
                                  </h5>
                                  <p className="small text-muted">
                                    {policyStr}
                                  </p>
                                </div>
                              );
                            }
                          })}
                        </>
                      ) : (
                        <>
                          <div className="policy-block">
                            <h5>Cancellation Policies</h5>
                            {roomData.length > 0 ? (
                              <div className="policy-info-box">
                                <p>
                                  Standard cancellation rules for this property:
                                </p>
                                <div className="policy-item">
                                  <i className="fa fa-info-circle"></i>
                                  <span>
                                    {formatCancellationPolicy(
                                      roomData[0].cancellationPolicy,
                                    )}
                                  </span>
                                </div>
                                <p className="mt-3 small text-muted">
                                  Note: Cancellation policies may vary by room
                                  type. Please check individual room details for
                                  specific policies.
                                </p>
                              </div>
                            ) : (
                              <p>
                                No specific cancellation policies available at
                                this time.
                              </p>
                            )}
                          </div>

                          {/* Dynamic Static Policies from Description */}
                          {hotelData.description?.onsite_payments && (
                            <div className="policy-block">
                              <h5>Payment & Methods</h5>
                              <div className="policy-item">
                                <i className="fas fa-credit-card"></i>
                                <span>
                                  Accepted:{" "}
                                  {hotelData.description.onsite_payments}
                                </span>
                              </div>
                            </div>
                          )}

                          {hotelData.description?.business_amenities && (
                            <div className="policy-block">
                              <h5>Property Details</h5>
                              <p className="small text-muted mb-0">
                                {hotelData.description.business_amenities}
                              </p>
                            </div>
                          )}

                          <div className="policy-block">
                            <h5>Check-in & Guest Requirements</h5>
                            {hotelData.description?.location && (
                              <p className="small text-muted mb-3">
                                {hotelData.description.location}
                              </p>
                            )}
                            <div className="policy-item">
                              <i className="fas fa-id-card"></i>
                              <span>
                                A valid photo ID is required for all guests at
                                the time of check-in.
                              </span>
                            </div>
                            <div className="policy-item">
                              <i className="fas fa-clock"></i>
                              <span>
                                Standard check-m is 2:00 PM and check-out is
                                11:00 AM.
                              </span>
                            </div>
                          </div>
                        </>
                      )}

                      {hotelData.amenities &&
                        hotelData.amenities.includes("Smoke-free property") && (
                          <div className="policy-block">
                            <h5>Property Rules</h5>
                            <div className="policy-item">
                              <i className="fas fa-smoking-ban"></i>
                              <span>
                                This is a smoke-free property. Smoking is
                                prohibited in all indoor areas.
                              </span>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cancellation Policy Modal (Premium Design) */}
      {showCancellationModal && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.6)",
            zIndex: 1050,
          }}
          onClick={() => setShowCancellationModal(false)}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content border-0 shadow-lg modal-content-premium">
              <div className="modal-header bg-light border-0 py-3">
                <h5 className="modal-title fw-bold" style={{ color: "#333" }}>
                  Room with Cancellation Policy
                </h5>
                <button
                  type="button"
                  className="btn-close shadow-none"
                  onClick={() => setShowCancellationModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4">
                {(() => {
                  const policies = Array.isArray(selectedCancellationPolicy)
                    ? selectedCancellationPolicy
                    : [];
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  // Find a free policy that is still valid (toDate >= today)
                  const activeFreePolicy = policies.find(
                    (p) =>
                      p.penaltyAmount === 0 &&
                      p.toDate &&
                      new Date(p.toDate) >= today,
                  );

                  const isRoomCurrentlyRefundable = !!activeFreePolicy;

                  const deadlineDate = activeFreePolicy
                    ? formatPolicyDate(activeFreePolicy.toDate, true, "12:00 AM")
                    : "";

                  const deadlineDateTimeline = activeFreePolicy
                    ? formatPolicyDate(activeFreePolicy.toDate, false, "12:00 AM")
                    : "";

                  const checkInDisplay = formatPolicyDate(searchData.checkInDate, false, "12:00 PM");

                  return (
                    <>
                      <style>{`
                        .timeline-segment-success {
                          flex: 1;
                          background: #28a745;
                          color: white;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          font-size: 11px;
                          font-weight: bold;
                          transition: all 0.3s ease;
                        }
                        .timeline-segment-danger {
                          flex: 1;
                          background: #dc3545;
                          color: white;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          font-size: 11px;
                          font-weight: bold;
                          transition: all 0.3s ease;
                        }
                        .x-small { font-size: 10px !important; }
                        .fs-12 { font-size: 12px !important; }
                      `}</style>
                      <div className="mb-4">
                        <h6 className="fw-bold small text-uppercase mb-3 text-secondary">
                          Cancellation Policy:
                        </h6>
                        <ul className="list-unstyled">
                          {isRoomCurrentlyRefundable ? (
                            <>
                              <li className="mb-2 d-flex align-items-center">
                                <i
                                  className="fas fa-check-circle text-success me-2"
                                  style={{ fontSize: "14px" }}
                                ></i>
                                <span className="small">
                                  Free Cancellation till {deadlineDate}
                                </span>
                              </li>
                              <li className="mb-2 d-flex align-items-center">
                                <i
                                  className="fas fa-info-circle text-warning me-2"
                                  style={{ fontSize: "14px" }}
                                ></i>
                                <span className="small">
                                  Penalty applies if canceled after {deadlineDate}
                                </span>
                              </li>
                            </>
                          ) : (
                            <li className="mb-2 d-flex align-items-center">
                              <i
                                className="fas fa-times-circle text-danger me-2"
                                style={{ fontSize: "14px" }}
                              ></i>
                              <span className="small fw-bold text-danger">
                                This booking is currently Non-Refundable
                              </span>
                            </li>
                          )}
                        </ul>
                      </div>

                      {/* Visual Timeline Bar */}
                      <div className="mb-5 mt-4">
                        <div
                          className="position-relative"
                          style={{ height: "40px" }}
                        >
                          <div
                            className="d-flex w-100 h-100 shadow-sm"
                            style={{ borderRadius: "20px", overflow: "hidden" }}
                          >
                            {isRoomCurrentlyRefundable ? (
                              <>
                                <div
                                  className="timeline-segment-success"
                                  style={{ flex: "0 0 50%" }}
                                >
                                  100% Refund
                                </div>
                                <div
                                  className="timeline-segment-danger"
                                  style={{ flex: "0 0 50%" }}
                                >
                                  Non-Refundable
                                </div>
                              </>
                            ) : (
                              <div
                                className="timeline-segment-danger"
                                style={{ flex: "0 0 100%" }}
                              >
                                Non-Refundable
                              </div>
                            )}
                          </div>

                          {/* Markers */}
                          <div className="d-flex justify-content-between mt-2 small text-muted px-2 fw-semibold x-small">
                            <span>Now</span>
                            <span>{deadlineDateTimeline || "Non-Refundable"}</span>
                            <div className="text-end">
                              <div>{checkInDisplay}</div>
                              <div className="fw-bold text-dark">Check-In</div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-5 pt-2">
                          <p className="small text-muted fst-italic">
                            {isRoomCurrentlyRefundable
                              ? `• Free Cancellation (100% refund) if you cancel this booking before ${deadlineDate}`
                              : `• This booking is Non-Refundable.`}
                          </p>
                        </div>
                      </div>

                      <div className="mt-4">
                        <h6
                          className="fw-bold mb-3 small"
                          style={{ color: "#333" }}
                        >
                          Cancellation post that will be subject to a fees as
                          follows:
                        </h6>
                        <div className="table-responsive rounded shadow-sm border">
                          <table className="table table-sm table-hover mb-0 fs-12">
                            <thead className="bg-light">
                              <tr>
                                <th className="py-2 px-3 border-0">
                                  Cancellation On or After
                                </th>
                                <th className="py-2 px-3 border-0">
                                  Cancellation On or Before
                                </th>
                                <th className="py-2 px-3 border-0">
                                  Cancellation Charges/Comments
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {policies.length > 0 ? (
                                policies.map((p, i) => (
                                  <tr key={i}>
                                    <td className="py-2 px-3">
                                      {formatDate(p.fromDate)}
                                    </td>
                                    <td className="py-2 px-3">
                                      {formatDate(p.toDate)}
                                    </td>
                                    <td className="py-2 px-3 fw-bold">
                                      {p.penaltyAmount === 0
                                        ? "₹ 0"
                                        : `${p.currency || "₹"} ${Math.round(p.penaltyAmount).toLocaleString()}`}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan="3"
                                    className="text-center py-3 text-muted"
                                  >
                                    No policy data available
                                  </td>
                                </tr>
                              )}
                              <tr className="bg-light">
                                <td
                                  colSpan="3"
                                  className="text-center py-2 text-muted x-small"
                                  style={{ fontSize: "11px" }}
                                >
                                  No Show will attract full cancellation charge
                                  unless otherwise specified.
                                </td>
                              </tr>
                              <tr className="bg-light">
                                <td
                                  colSpan="3"
                                  className="text-center py-2 text-muted x-small"
                                >
                                  Early check out will attract full cancellation
                                  charge unless otherwise specified.
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer border-0 p-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4 rounded-pill btn-sm d-none"
                  onClick={() => setShowCancellationModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HotelDetail;
